// ============================================================
// panelUrlSyncCore — usePanelUrlSync の純関数部（react-router 非依存）
// ============================================================
// jest (CRA5) が react-router-dom v7 の ESM を解決できないため、
// テスト対象の判定ロジック・検索ヘルパーをフック本体から分離している。
// regression テスト: src/hooks/__tests__/usePanelUrlSync.test.ts

/** 1回の同期で取るべきアクション（decideSyncAction の戻り値） */
export type SyncAction =
  | { type: 'none' }
  /** state → URL: クエリを value に書き換える（value=null はクエリ除去）。push=true なら履歴を積む */
  | { type: 'write-url'; value: string | null; push: boolean }
  /** URL → state: value を resolve してパネルを開く（解決失敗時は呼び出し側でクエリ除去） */
  | { type: 'open-from-url'; value: string }
  /** URL → state: クエリが消えたのでパネルを閉じる */
  | { type: 'close-from-url' };

/**
 * state と URL の食い違いをどちら向きに解決するか決める純関数。
 * 「state が前回から変わった」なら state → URL、そうでなければ URL → state。
 * 初回（isFirstRun）は URL を真実とする（ディープリンク復元）。
 */
export function decideSyncAction(params: {
  isFirstRun: boolean;
  stateVal: string | null;
  lastStateVal: string | null;
  urlVal: string | null;
}): SyncAction {
  const { isFirstRun, stateVal, lastStateVal, urlVal } = params;
  const stateChanged = !isFirstRun && stateVal !== lastStateVal;

  if (stateChanged) {
    if (urlVal === stateVal) return { type: 'none' };
    // 閉→開 は push（バックで閉じられる）。切替・閉じるは replace。
    const isOpenFromClosed = urlVal === null && stateVal !== null;
    return { type: 'write-url', value: stateVal, push: isOpenFromClosed };
  }

  if (urlVal === stateVal) return { type: 'none' };
  if (urlVal !== null) return { type: 'open-from-url', value: urlVal };
  return { type: 'close-from-url' };
}

/** 予約を URL 値で検索。id 一致を優先し、次に予約番号 (contact_code) */
export function findReservationByParam<
  R extends { id: string | number; contact_code?: string }
>(reservations: R[], value: string): R | null {
  const v = value.trim();
  if (v === '') return null;
  for (const r of reservations) {
    if (String(r.id) === v) return r;
  }
  for (const r of reservations) {
    if (r.contact_code && String(r.contact_code) === v) return r;
  }
  return null;
}

/** 車両を URL 値で検索。id → 車両番号 (vehicle_number) → 車両コードの順 */
export function findVehicleByParam<
  V extends { id: string | number; vehicle_number?: string; vehicle_code?: string }
>(vehicles: V[], value: string): V | null {
  const v = value.trim();
  if (v === '') return null;
  for (const x of vehicles) {
    if (String(x.id) === v) return x;
  }
  for (const x of vehicles) {
    if (x.vehicle_number && String(x.vehicle_number) === v) return x;
  }
  for (const x of vehicles) {
    if (x.vehicle_code && String(x.vehicle_code) === v) return x;
  }
  return null;
}
